import { call, put, select } from 'redux-saga/effects';
import { selectSelectedLanguage } from '../userData/selectors';
import { api } from '../utils/api';
import etrApi from '../utils/etrApi';
import {
  enqueueErrorSnackbar,
  enqueueSuccessSnackbar,
} from '../notifications/actionWrapper';
import { translate } from '../../utils/translation';
import { mapProjectForBulkPayload, mapProjectForPaylaod } from './types';
import msTeamsApi from '../utils/msTeamsApi';
import { replaceSlashes } from './utils';
import { checkForHTTPError, returnSnackbarMessage } from '../utils/utils';
import { fetchChangeLog } from '../changelog/actionWrapper';
import { changeLogActions } from '../changelog/slice';
import {
  shouldAutoPromoteToDraft,
  projectFromReltp,
} from '../../utils/fieldEditingRules';
import { getProjects } from './selectors';

export const fetchProjectsSagaFactory = (actions) => {
  return function* ({ payload }) {
    try {
      const { data } = yield etrApi.post('/ppm/unit-projects', payload);

      yield put(actions.fetchProjectsSuccess(data));
    } catch (e) {
      console.error(e);
      yield put(actions.fetchProjectsError(e));
    }
  };
};

export const fetchProjectSagaFactory = (actions) => {
  return function* ({ payload }) {
    const lang = yield select(selectSelectedLanguage);
    const url = payload.refId
      ? `/ppm/unit-project/ref/${payload.id}`
      : `/ppm/unit-project/${payload.id}`;
    try {
      const { data, status } = yield etrApi.get(url);
      checkForHTTPError({ status });

      yield put(
        actions.fetchProjectSuccess({
          ...data,
          alignedGex: data?.alignedGex || [{ fy: 'FY24', value: '' }],
          investments: data?.investments || [], // we are getting null as value for this field and it breaks logic in library. it can't hanlde null value
          dontAddToProjects: payload.dontAddToProjects,
          // todo-ppm remvoe gex placeholder
        }),
      );
      yield put(changeLogActions.resetPages());
      yield put(fetchChangeLog(data.id));
    } catch (e) {
      console.error(e);
      yield put(
        enqueueErrorSnackbar(
          returnSnackbarMessage(
            e,
            translate('snackbarMessages.couldNotFetchProjects', lang),
          ),
        ),
      );
      yield put(actions.fetchProjectFailed(e));
    }
  };
};

export const fetchProjectActivityLogSagaFactory = (actions) => {
  return function* ({ payload }) {
    const lang = yield select(selectSelectedLanguage);
    try {
      const { data, status } = yield etrApi.get(
        `ppm/unit-project/${payload.id}/changelog?page=0&size=100`,
      );
      checkForHTTPError({ status });
      yield put(
        actions.fetchProjectActivityLogSuccess({
          ...data,
        }),
      );
    } catch (e) {
      console.error(e);
      yield put(
        enqueueErrorSnackbar(
          returnSnackbarMessage(
            e,
            translate('snackbarMessages.couldNotFetchProjects', lang),
          ),
        ),
      );
      yield put(actions.fetchProjectFailed(e));
    }
  };
};

export function fetchProjectsByStatusSagaFactory(actions, replace) {
  return function* ({ payload }) {
    const lang = yield select(selectSelectedLanguage);
    let nextPayload;
    let requestPayload;
    if (Array.isArray(payload)) {
      nextPayload = payload.slice(1);
      requestPayload = payload[0];
    } else {
      requestPayload = payload;
    }
    try {
      const { data } = yield api.get(`projects-list?status=${requestPayload}`);
      if (data) {
        replace
          ? yield put(actions.fetchProjectsByStatusReplaceSuccess(data))
          : yield put(actions.fetchProjectsByStatusSuccess(data));
      }
    } catch (e) {
      yield put(actions.fetchProjectsByStatusError(e.message));
      yield put(
        enqueueErrorSnackbar(
          returnSnackbarMessage(
            e,
            translate('snackbarMessages.problemGettingUpdatedProjects', lang),
          ),
        ),
      );
    } finally {
      if (nextPayload?.length) {
        yield put(actions.fetchProjectsByStatus(nextPayload));
      }
    }
  };
}

export function updateProjectsStatusSagaFactory(actions) {
  return function* ({ payload }) {
    const lang = yield select(selectSelectedLanguage);
    try {
      yield etrApi.patch('ppm/unit-projects/status', payload);
      yield put(actions.updateProjectsStatusSuccess(payload));
      yield put(
        enqueueSuccessSnackbar(translate('snackbarMessages.updated', lang)),
      );
    } catch (e) {
      yield put(actions.updateProjectsStatusFailed(e.message));
      yield put(
        enqueueErrorSnackbar(
          returnSnackbarMessage(
            e,
            translate('snackbarMessages.problemGettingUpdatedProjects', lang),
          ),
        ),
      );
      yield put(actions.updateProjectsStatusFailed(e.message));
    }
  };
}

export function updateProjectStatusSagaFactory(actions) {
  return function* ({ payload }) {
    const lang = yield select(selectSelectedLanguage);
    try {
      yield etrApi.patch(`ppm/unit-project/${payload.projectId}/status`, {
        value: payload.status,
      });
      yield put(
        enqueueSuccessSnackbar(translate('snackbarMessages.updated', lang)),
      );
      yield put(actions.updateProjectStatusSuccess(payload));
    } catch (e) {
      yield put(
        enqueueErrorSnackbar(
          returnSnackbarMessage(
            e,
            translate('snackbarMessages.problemGettingUpdatedProjects', lang),
          ),
        ),
      );
      yield put(actions.updateProjectStatusFailed(e.message));
    }
  };
}

export function createProjectSagaFactory(actions) {
  return function* ({ payload }) {
    const lang = yield select(selectSelectedLanguage);
    try {
      const promotedPayload = shouldAutoPromoteToDraft(
        payload.projectStatus,
        payload.partOfCrp,
        payload.projectSource,
      )
        ? { ...payload, projectStatus: 'DRAFT' }
        : payload;
      const { data } = yield etrApi.post(
        '/ppm/unit-project',
        mapProjectForPaylaod(promotedPayload),
      );
      if (!data) throw new Error('No data returned');
      yield put(actions.createNewProjectSuccess(data));
      yield put(enqueueSuccessSnackbar('Project created successfully'));
    } catch (e) {
      console.error(e);
      yield put(actions.createNewProjectFailed(e));
      yield put(
        enqueueErrorSnackbar(
          returnSnackbarMessage(
            e,
            translate('snackbarMessages.problemCreatingProject', lang),
          ),
        ),
      );
    }
  };
}

export function updateProjectSagaFactory(actions) {
  return function* ({ payload }) {
    const lang = yield select(selectSelectedLanguage);
    try {
      const project = payload.project;
      const promotedProject = shouldAutoPromoteToDraft(
        project.projectStatus,
        project.partOfCrp,
        project.projectSource,
      )
        ? { ...project, projectStatus: 'DRAFT' }
        : project;
      const { data } = yield etrApi.put(
        `/ppm/unit-project`,
        mapProjectForPaylaod(promotedProject),
      );
      if (!data) throw new Error('No data returned');
      yield put(actions.updateProjectSuccess(data));
      yield put(
        enqueueSuccessSnackbar(translate('snackbarMessages.updated', lang)),
      );
      if (payload.action) {
        yield put(payload.action);
      }
    } catch (e) {
      console.error(e);
      yield put(actions.updateProjectError(e.message));
      yield put(
        enqueueErrorSnackbar(
          returnSnackbarMessage(
            e,
            translate('snackbarMessages.problemUpdatingProject', lang),
          ),
        ),
      );
    }
  };
}

export function fetchSiteLocationSagaFactory(actions) {
  return function* ({ payload }) {
    const lang = yield select(selectSelectedLanguage);
    try {
      const { data } = yield etrApi.get(`/ppm/site-location/${payload}`);
      if (!data) throw new Error('No data returned');
      yield put(actions.setSiteLocation(data));
    } catch (e) {
      console.error(e);
      yield put(
        enqueueErrorSnackbar(
          returnSnackbarMessage(
            e,
            translate('snackbarMessages.fetchSiteLocationError', lang),
          ),
        ),
      );
    }
  };
}

export function fetchSiteLocationForBu() {
  return function* ({ payload }) {
    try {
      const { bu, onSuccess } = payload;
      const { data } = yield etrApi.get(`/ppm/site-location/${bu}`);
      if (onSuccess) {
        yield call(onSuccess, data);
      }
    } catch (e) {
      const lang = yield select(selectSelectedLanguage);
      enqueueErrorSnackbar(
        returnSnackbarMessage(
          e,
          translate('snackbarMessages.fetchSiteLocationError', lang),
        ),
      );
    }
  };
}

export function approveCrpSagaFactory(actions) {
  return function* ({ payload }) {
    const lang = yield select(selectSelectedLanguage);
    try {
      const response = yield etrApi.patch('/ppm/unit-projects/crp', payload);
      if (response?.status !== 200) throw new Error('No data returned');
      yield put(actions.approveCrpSuccess(payload));
      yield put(
        enqueueSuccessSnackbar(translate('snackbarMessages.updated', lang)),
      );
    } catch (e) {
      console.error(e);
      yield put(actions.approveCrpFailed(e.message));
      yield put(
        enqueueErrorSnackbar(
          returnSnackbarMessage(
            e,
            translate('snackbarMessages.problemApprovingProject', lang),
          ),
        ),
      );
    }
  };
}

export function updatePartOfCrpSagaFactory(actions) {
  return function* ({ payload }) {
    const lang = yield select(selectSelectedLanguage);
    try {
      // Get projects before update to check current status
      const allProjects = yield select(getProjects);

      const response = yield etrApi.patch(
        '/ppm/unit-projects/update-part-of-crp',
        payload,
      );
      if (response?.status !== 200) throw new Error('No data returned');
      yield put(actions.updatePartOfCrpSuccess(response.data));
      yield put(
        enqueueSuccessSnackbar(translate('snackbarMessages.updated', lang)),
      );

      // Auto-promote Qualified/Unqualified non-RELTP projects to Draft
      if (payload.fiscalYears?.length > 0) {
        const projectsToPromote = payload.projectIds.filter((id) => {
          const project = allProjects.find((p) => p.id === id);
          return (
            project &&
            !projectFromReltp(project.projectSource) &&
            (project.projectStatus === 'QUALIFIED' ||
              project.projectStatus === 'UNQUALIFIED')
          );
        });

        if (projectsToPromote.length > 0) {
          try {
            yield etrApi.patch('ppm/unit-projects/status', {
              projects: projectsToPromote,
              status: 'DRAFT',
            });
            yield put(
              actions.updateProjectsStatusSuccess({
                projects: projectsToPromote,
                status: 'DRAFT',
              }),
            );
          } catch (statusError) {
            console.error(
              'Failed to auto-promote project status to Draft:',
              statusError,
            );
          }
        }
      }
    } catch (e) {
      console.error(e);
      yield put(actions.updatePartOfCrpFailed(e.message));
      yield put(
        enqueueErrorSnackbar(
          returnSnackbarMessage(
            e,
            translate('snackbarMessages.problemUpdatingPartOfCrp', lang),
          ),
        ),
      );
    }
  };
}

export function updatePartOfApprovedCrpSagaFactory(actions) {
  return function* ({ payload }) {
    const lang = yield select(selectSelectedLanguage);
    try {
      const response = yield etrApi.patch(
        '/ppm/unit-projects/update-part-of-approved-crp',
        payload,
      );
      if (response?.status !== 200) throw new Error('No data returned');
      yield put(actions.updatePartOfApprovedCrpSuccess(response.data));
      yield put(
        enqueueSuccessSnackbar(translate('snackbarMessages.updated', lang)),
      );
    } catch (e) {
      console.error(e);
      yield put(actions.updatePartOfApprovedCrpFailed(e.message));
      yield put(
        enqueueErrorSnackbar(
          returnSnackbarMessage(
            e,
            translate(
              'snackbarMessages.problemUpdatingPartOfApprovedCrp',
              lang,
            ),
          ),
        ),
      );
    }
  };
}

export function archiveProjectSagaFactory(actions) {
  return function* ({ payload }) {
    const lang = yield select(selectSelectedLanguage);
    try {
      const { data } = yield etrApi.put(
        '/ppm/unit-projects/archive?action=ARCHIVE',
        payload,
      );

      if (data.length > 0) {
        yield put(actions.archiveProjectSuccess(data));
      } else {
        yield put(
          enqueueErrorSnackbar(
            translate('snackbarMessages.couldNotArchiveProject', lang),
          ),
        );
      }
    } catch (error) {
      yield put(
        enqueueErrorSnackbar(
          returnSnackbarMessage(
            error,
            translate('snackbarMessages.couldNotArchiveProject', lang),
          ),
        ),
      );
      yield put(actions.archiveProjectError(error.message));
    }
  };
}
export function deleteProjectSagaFactory(actions) {
  return function* ({ payload }) {
    const lang = yield select(selectSelectedLanguage);
    try {
      yield etrApi.delete(`/ppm/unit-project/${payload}`);

      yield put(actions.deleteProjectSuccess(payload));
    } catch (error) {
      yield put(
        enqueueErrorSnackbar(
          returnSnackbarMessage(
            error,
            translate('snackbarMessages.couldNotDeleteProject', lang),
          ),
        ),
      );
      yield put(actions.deleteProjectError(error.message));
    }
  };
}

export function fetchProjectsForBulkSagaFactory(actions) {
  return function* ({ payload }) {
    const lang = yield select(selectSelectedLanguage);
    try {
      const { data } = yield etrApi.post('/ppm/bulk/unit-projects', {
        projectIds: payload,
      });
      yield put(actions.updateProjectsBulkSuccess(data.projects));
    } catch (error) {
      yield put(
        enqueueErrorSnackbar(
          returnSnackbarMessage(
            error,
            translate('snackbarMessages.couldNotFetchProjects', lang),
          ),
        ),
      );
      yield put(actions.fetchProjectsForBulkError(error.message));
    }
  };
}

export function updateProjectsBulkSagaFactory(actions) {
  return function* ({ payload }) {
    const lang = yield select(selectSelectedLanguage);
    try {
      const { data } = yield etrApi.put('/ppm/bulk/unit-projects', {
        projects: payload.payload.map(mapProjectForBulkPayload),
      });
      yield put(actions.updateProjectsBulkSuccess(data.projects));
      payload.history.push('/');
      yield put(enqueueSuccessSnackbar('Projects updated successfully'));
    } catch (error) {
      yield put(
        enqueueErrorSnackbar(
          returnSnackbarMessage(
            error,
            translate('snackbarMessages.couldNotUpdateProjects', lang),
          ),
        ),
      );
      yield put(actions.updateProjectsBulkError(error.message));
    }
  };
}

export function addToOnePlanSagaFactory(actions) {
  return function* ({ payload }) {
    const lang = yield select(selectSelectedLanguage);
    try {
      const response = yield etrApi.put(
        `/ppm/unit-project/${payload}?part-of-one-plan=true`,
      );
      if (response.status !== 200) throw new Error('No data returned');
      yield put(actions.addToOnePlanSuccess());
    } catch (error) {
      yield put(
        enqueueErrorSnackbar(
          returnSnackbarMessage(
            error,
            translate('snackbarMessages.couldNotEditOnePlan', lang),
          ),
        ),
      );
      yield put(actions.addToOnePlanFailed(error.message));
    }
  };
}

export function createMSTeamsSagaFactory(actions) {
  return function* ({ payload }) {
    const lang = yield select(selectSelectedLanguage);
    try {
      let teamsUrl = payload.link;

      if (!teamsUrl) {
        const response = yield msTeamsApi.post(`/copy-team`, {
          teamName: `${payload.referenceId} - ${payload.projectName}`,
          teamDescription: `${payload.referenceId} - ${payload.projectName}`,
          isTesting: process.env.NODE_ENV === 'development',
          ownerEmail: payload.ownerEmail,
          members: payload.members,
        });

        if (response.status !== 200) throw new Error('No data returned');
        teamsUrl = response.data.teamsUrl;
      }

      yield etrApi.post(`/ppm/unit-project/${payload.projectId}/ms-teams-url`, {
        msTeamsUrl: replaceSlashes(teamsUrl, '____'),
      });
      yield put(actions.createMSTeamsSuccess(teamsUrl));
    } catch (error) {
      yield put(
        enqueueErrorSnackbar(
          returnSnackbarMessage(
            error,
            translate('snackbarMessages.couldNotCreateMSTeams', lang),
          ),
        ),
      );
      yield put(actions.createMSTeamsFailed(error.message));
    }
  };
}

export function bulkCreateSagaFactory(actions) {
  return function* ({ payload }) {
    const lang = yield select(selectSelectedLanguage);
    try {
      const { data } = yield etrApi.post('/ppm/unit-projects/bulk', {
        projects: payload.projects,
      });
      yield put(actions.createProjectsBulkSuccess(data.projects));
      yield put(
        enqueueSuccessSnackbar(
          `Successfully created ${payload.projects.length} projects`,
        ),
      );
      payload.history.push('/');
    } catch (error) {
      yield put(actions.createProjectsBulkError(error.message));
      yield put(
        enqueueErrorSnackbar(
          returnSnackbarMessage(
            error,
            translate('snackbarMessages.couldNotCreateProjects', lang),
          ),
        ),
      );
    }
  };
}
